import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Check, Brain, GitBranch, Radar, Globe, Trash2, AlertTriangle, Sparkles } from 'lucide-react';
import type {
  EndpointConfig,
  ProjectType,
  RepoConfig,
  EndpointAuthKind,
  EndpointSpecKind,
  RepoProvider,
  RepoAccessMethod,
  ProjectPrincipal,
  PrincipalRole,
  BusinessFlow,
  SiblingEnvironment,
  WebappPentestConfig,
} from '@nirvik/shared';
import { DEFAULT_ROE } from '@nirvik/shared';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { DomainVerificationPanel } from '@/components/domain/domain-verification-panel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AllowedModel {
  id: string;
  name: string;
  pricePerMInput: number;
  pricePerMOutput: number;
}

interface AgentConfigData {
  repoUrl: string | null;
  branch: string;
  llmModel: string | null;            // project's pinned override (null = use org default)
  effectiveProvider: string | null;   // resolved at run time, read-only here
  orgDefaultModel: string | null;     // shown on the "Use org default" card
  allowedModels: AllowedModel[];      // catalog metadata for the picker
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ProjectSettingsPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();
  const enabled = !!orgId && !!portfolioId && !!projectId;
  const basePath = `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}`;
  const queryClient = useQueryClient();

  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // ---- Query: load agent config ----
  const { data: configRes, isLoading } = useQuery({
    queryKey: ['agent-configs', orgId, portfolioId, projectId],
    queryFn: () => apiClient.get(`${basePath}/agents/config`),
    enabled,
  });

  const configData = configRes?.data as AgentConfigData | undefined;
  const allowedModels = configData?.allowedModels ?? [];
  const orgDefaultModel = configData?.orgDefaultModel ?? null;
  const effectiveProvider = configData?.effectiveProvider ?? null;

  // Drop a stale pinned model that's no longer in the org's allowed list.
  const allowedIds = new Set(allowedModels.map((m) => m.id));
  const cleanSavedModel = configData?.llmModel && allowedIds.has(configData.llmModel) ? configData.llmModel : null;

  useEffect(() => {
    if (configData) {
      setSelectedModel(cleanSavedModel);
      setIsDirty(false);
    }
  }, [configData, cleanSavedModel]);

  // ---- Mutation: save config ----
  const saveConfig = useMutation({
    mutationFn: (model: string | null) =>
      apiClient.put(`${basePath}/agents/config`, {
        repoUrl: configData?.repoUrl || '',
        branch: configData?.branch || 'main',
        llmModel: model,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-configs', orgId, portfolioId, projectId] });
      setIsDirty(false);
    },
  });

  function handleSelect(modelId: string | null) {
    setSelectedModel(modelId);
    setIsDirty(modelId !== cleanSavedModel);
  }

  function handleSave() {
    saveConfig.mutate(selectedModel);
  }

  // ---- Loading ----
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-muted rounded w-48 mb-2" />
        <div className="h-4 bg-gray-100 dark:bg-muted rounded w-72" />
        <Card className="p-6 animate-pulse">
          <div className="h-10 bg-gray-100 dark:bg-muted rounded w-full mb-3" />
          <div className="h-10 bg-gray-100 dark:bg-muted rounded w-2/3" />
        </Card>
      </div>
    );
  }

  const orgDefaultDisplay = allowedModels.find((m) => m.id === orgDefaultModel)?.name ?? orgDefaultModel ?? '— not set —';

  // ---- Render ----
  return (
    <div className="space-y-8">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">
          Project Settings
        </h1>
        <p className="text-muted-foreground dark:text-muted-foreground mt-1">
          Configure how AI agents analyze this project
        </p>
      </div>

      {/* Model override */}
      <Card className="bg-card border border-border rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
            <Brain className="h-5 w-5 text-indigo-700 dark:text-indigo-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-foreground">Model override</h2>
            <p className="text-sm text-muted-foreground">
              Pin a specific model for this project, or fall back to the org default. The provider is chosen at the org level.
            </p>
          </div>
        </div>

        {/* Read-only provider banner */}
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 mb-5 flex items-center gap-2 flex-wrap">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Provider</span>
          <span className="text-sm font-mono text-foreground">{effectiveProvider ?? '— none available —'}</span>
          <span className="text-xs text-muted-foreground ml-auto">Set in org settings</span>
        </div>

        {allowedModels.length === 0 ? (
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground mb-5">
            No models are available for this org yet. Ask an org admin to enable a provider and pick allowed models.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {/* "Use org default" card */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className={cn(
                'relative flex items-start gap-3 rounded-xl border p-4 text-left transition-all',
                selectedModel === null
                  ? 'border-primary/40 bg-primary/5 ring-2 ring-primary/20'
                  : 'border-border bg-card/80 hover:bg-card',
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">Use org default</p>
                <p className="text-xs text-muted-foreground mt-0.5 truncate">{orgDefaultDisplay}</p>
                <p className="text-[11px] text-muted-foreground mt-1">No project-level override.</p>
              </div>
              {selectedModel === null && (
                <Check className="h-4 w-4 text-primary shrink-0" />
              )}
            </button>

            {allowedModels.map((m) => {
              const isSelected = selectedModel === m.id;
              const isOrgDefault = orgDefaultModel === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleSelect(m.id)}
                  className={cn(
                    'relative flex items-start gap-3 rounded-xl border p-4 text-left transition-all',
                    isSelected
                      ? 'border-primary/40 bg-primary/5 ring-2 ring-primary/20'
                      : 'border-border bg-card/80 hover:bg-card',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground flex items-center gap-2 flex-wrap">
                      {m.name}
                      {isOrgDefault && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-saffron/15 text-saffron">
                          org default
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">{m.id}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      ${m.pricePerMInput}/M in · ${m.pricePerMOutput}/M out
                    </p>
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Save button */}
        <div className="flex items-center gap-3">
          <Button
            disabled={!isDirty || saveConfig.isPending}
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            {saveConfig.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : saveConfig.isSuccess && !isDirty ? (
              <Check className="h-4 w-4 mr-2" />
            ) : null}
            {saveConfig.isSuccess && !isDirty ? 'Saved' : 'Save model'}
          </Button>
          {saveConfig.isError && (
            <p className="text-xs text-red-700 dark:text-red-400">
              {(saveConfig.error as Error).message}
            </p>
          )}
        </div>
      </Card>

      {enabled && <TargetConfigSection orgId={orgId!} portfolioId={portfolioId!} projectId={projectId!} />}
      {enabled && <DomainVerificationPanel orgId={orgId!} portfolioId={portfolioId!} projectId={projectId!} />}
      {enabled && <DangerZone orgId={orgId!} portfolioId={portfolioId!} projectId={projectId!} />}
    </div>
  );
}

// ─── Danger Zone — destructive actions ────────────────────────────────────

function DangerZone({ orgId, portfolioId, projectId }: { orgId: string; portfolioId: string; projectId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const { data: projectRes } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId, portfolioId, projectId),
  });
  const projectName = (projectRes?.data as { name?: string } | undefined)?.name ?? '';

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteProject(orgId, portfolioId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', orgId, 'portfolio', portfolioId, 'projects'] });
      queryClient.removeQueries({ queryKey: ['project', orgId, portfolioId, projectId] });
      setOpen(false);
      navigate(`/orgs/${orgId}/portfolios/${portfolioId}`);
    },
  });

  const canConfirm = projectName.length > 0 && confirmText === projectName;

  return (
    <Card className="bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/30 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-foreground">Danger Zone</h2>
          <p className="text-sm text-muted-foreground dark:text-muted-foreground">
            Destructive actions that cannot be undone
          </p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-foreground">Delete this project</p>
          <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-0.5">
            Permanently removes the project record. Scans, findings, and agent runs stored
            against this project will remain in the database for now (handled by a separate
            cleanup job).
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setConfirmText('');
            setOpen(true);
          }}
          className="border-red-300 dark:border-red-500/40 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/10 shrink-0"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete project
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this project?</DialogTitle>
            <DialogDescription>
              This cannot be undone. To confirm, type the project name exactly:{' '}
              <span className="font-mono font-semibold text-gray-900 dark:text-foreground">
                {projectName}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="confirmName">Project name</Label>
            <Input
              id="confirmName"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={projectName}
              autoFocus
            />
          </div>

          {deleteMutation.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {(deleteMutation.error as Error).message}
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => deleteMutation.mutate()}
              disabled={!canConfirm || deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Target configuration section ─────────────────────────────────────────

function TargetConfigSection({ orgId, portfolioId, projectId }: { orgId: string; portfolioId: string; projectId: string }) {
  const queryClient = useQueryClient();
  const { data: projectRes, isLoading } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId, portfolioId, projectId),
  });

  const project = projectRes?.data as
    | {
        projectType?: ProjectType;
        repoConfig?: RepoConfig;
        endpointConfig?: EndpointConfig;
      }
    | undefined;

  // Fall back to 'repo' for legacy projects created before the type field existed.
  const projectType: ProjectType = project?.projectType ?? 'repo';

  const [repoConfig, setRepoConfig] = useState<RepoConfig | undefined>(project?.repoConfig);
  const [endpointConfig, setEndpointConfig] = useState<EndpointConfig | undefined>(project?.endpointConfig);

  useEffect(() => {
    setRepoConfig(project?.repoConfig);
    setEndpointConfig(project?.endpointConfig);
  }, [project?.repoConfig, project?.endpointConfig]);

  const saveTarget = useMutation({
    mutationFn: (payload: { repoConfig?: RepoConfig; endpointConfig?: EndpointConfig }) =>
      apiClient.updateProject(orgId, portfolioId, projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', orgId, portfolioId, projectId] });
    },
  });

  if (isLoading) return null;

  return (
    <Card className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
          {projectType === 'endpoint' ? (
            <Radar className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
          ) : projectType === 'hybrid' ? (
            <Globe className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
          ) : (
            <GitBranch className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
          )}
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">Target Configuration</h2>
          <p className="text-sm text-muted-foreground">
            {projectType === 'repo' && 'Source repository this project analyzes'}
            {projectType === 'endpoint' && 'Live endpoint red-team agents will probe'}
            {projectType === 'hybrid' && 'Both repo and endpoint configured for this project'}
          </p>
        </div>
        <span className="ml-auto text-xs uppercase tracking-wide px-2 py-1 rounded-full bg-muted text-foreground border border-border">
          {projectType}
        </span>
      </div>

      {(projectType === 'repo' || projectType === 'hybrid') && (
        <RepoConfigEditor
          value={repoConfig}
          onChange={setRepoConfig}
          onSave={() => saveTarget.mutate({ repoConfig })}
          saving={saveTarget.isPending}
        />
      )}

      {(projectType === 'endpoint' || projectType === 'hybrid') && (
        <EndpointConfigEditor
          value={endpointConfig}
          onChange={setEndpointConfig}
          onSave={() => saveTarget.mutate({ endpointConfig })}
          saving={saveTarget.isPending}
          orgId={orgId}
          portfolioId={portfolioId}
          projectId={projectId}
        />
      )}
    </Card>
  );
}

function RepoConfigEditor({
  value,
  onChange,
  onSave,
  saving,
}: {
  value: RepoConfig | undefined;
  onChange: (v: RepoConfig) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const cfg = value ?? {
    provider: 'github' as RepoProvider,
    gitUrl: '',
    branch: 'main',
    accessMethod: 'public' as RepoAccessMethod,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Repository</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Provider</Label>
          <select
            value={cfg.provider}
            onChange={(e) => onChange({ ...cfg, provider: e.target.value as RepoProvider })}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
          >
            <option value="github">GitHub</option>
            <option value="gitlab">GitLab</option>
            <option value="bitbucket">Bitbucket</option>
            <option value="generic-git">Generic Git</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label>Access</Label>
          <select
            value={cfg.accessMethod}
            onChange={(e) => onChange({ ...cfg, accessMethod: e.target.value as RepoAccessMethod })}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
          >
            <option value="public">Public</option>
            <option value="github-app">GitHub App</option>
            <option value="deploy-key">Deploy key</option>
            <option value="pat">Personal access token</option>
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Git URL</Label>
        <Input
          value={cfg.gitUrl}
          onChange={(e) => onChange({ ...cfg, gitUrl: e.target.value })}
          placeholder="https://github.com/org/repo.git"
        />
      </div>

      <div className="space-y-2">
        <Label>Branch</Label>
        <Input value={cfg.branch} onChange={(e) => onChange({ ...cfg, branch: e.target.value })} />
      </div>

      {cfg.accessMethod === 'github-app' && (
        <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
          <div className="space-y-2">
            <Label>Auto-fix PRs</Label>
            <select
              value={cfg.autoFixPolicy ?? 'never'}
              onChange={(e) => onChange({ ...cfg, autoFixPolicy: e.target.value as 'never' | 'open-draft' | 'open-ready' })}
              className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
            >
              <option value="never">Never — agents will not open PRs</option>
              <option value="open-draft">Open as draft — reviewer promotes to ready</option>
              <option value="open-ready">Open ready-for-review</option>
            </select>
            <p className="text-[11px] text-muted-foreground">
              When agents (e.g. code-vuln) emit an auto-fixable patch, they may open a PR
              on this repo via the Trinetra GitHub App. Each PR explains the finding it
              resolves and links back to the run record.
            </p>
          </div>

          {(cfg.autoFixPolicy ?? 'never') !== 'never' && (
            <div className="space-y-2 pt-2 border-t border-border">
              <Label>Auto-fix grouping</Label>
              <select
                value={cfg.autoFixGrouping ?? 'per-finding'}
                onChange={(e) => onChange({ ...cfg, autoFixGrouping: e.target.value as 'per-finding' | 'combined' })}
                className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
              >
                <option value="per-finding">Per finding — one PR per fix (best for human review)</option>
                <option value="combined">Combined — single rolling PR with all fixes (best for automated PR review)</option>
              </select>
              <p className="text-[11px] text-muted-foreground">
                <strong>Per finding</strong> opens an isolated PR per fix on a fingerprint-derived branch — easier to
                review and merge one at a time. <strong>Combined</strong> rolls every fix onto a single evergreen
                <code className="mx-1 text-[10px]">trinetra/combined-fixes</code>branch with one PR — better for
                CI pipelines that gate on a single batch merge.
              </p>
            </div>
          )}
        </div>
      )}

      <Button disabled={saving} onClick={onSave} className="bg-emerald-600 hover:bg-emerald-500 text-white">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        Save repo config
      </Button>
    </div>
  );
}

function EndpointConfigEditor({
  value,
  onChange,
  onSave,
  saving,
  orgId,
  portfolioId,
  projectId,
}: {
  value: EndpointConfig | undefined;
  onChange: (v: EndpointConfig) => void;
  onSave: () => void;
  saving: boolean;
  orgId: string;
  portfolioId: string;
  projectId: string;
}) {
  const cfg = value ?? {
    baseUrl: '',
    hostnames: [],
    auth: { kind: 'none' as EndpointAuthKind },
    spec: { kind: 'none' as EndpointSpecKind },
    rulesOfEngagement: DEFAULT_ROE,
  };
  const [uploadErr, setUploadErr] = useState<string>('');
  const [uploading, setUploading] = useState(false);

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr('');
    setUploading(true);
    try {
      const res = await apiClient.specUploadUrl(orgId, portfolioId, projectId, file.type || 'application/json', cfg.spec.kind === 'none' ? 'openapi' : cfg.spec.kind);
      const data = res.data;
      if (!data) throw new Error('Failed to mint upload URL');
      const putRes = await fetch(data.url, {
        method: data.method,
        headers: data.headers,
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);
      onChange({
        ...cfg,
        spec: { ...cfg.spec, uploadedKey: data.key },
      });
    } catch (err) {
      setUploadErr(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-4 border-t border-border pt-5 mt-5 first:border-t-0 first:pt-0 first:mt-0">
      <div className="flex items-center gap-2">
        <Radar className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Endpoint</h3>
      </div>

      <div className="space-y-2">
        <Label>Base URL</Label>
        <Input
          value={cfg.baseUrl}
          onChange={(e) => onChange({ ...cfg, baseUrl: e.target.value })}
          placeholder="https://api.example.com"
        />
      </div>

      <div className="space-y-2">
        <Label>Additional in-scope hostnames</Label>
        <Input
          value={cfg.hostnames.join(', ')}
          onChange={(e) =>
            onChange({
              ...cfg,
              hostnames: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
          placeholder="api-staging.example.com, cdn.example.com"
        />
      </div>

      {/* ── Principals ─────────────────────────────────────────────
          N principals per project for multi-user / cross-tenant
          testing. Auth + credentials live per-principal; declarative
          fields are jsonb on the project, credential leaves are
          AES-256-GCM envelopes encrypted with the org's DEK. Saved
          via dedicated /principals routes so credentials never round
          through the project-row PATCH. */}
      <PrincipalsManager
        orgId={orgId}
        portfolioId={portfolioId}
        projectId={projectId}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>API spec</Label>
          <select
            value={cfg.spec.kind}
            onChange={(e) => onChange({ ...cfg, spec: { ...cfg.spec, kind: e.target.value as EndpointSpecKind } })}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
          >
            <option value="none">None</option>
            <option value="openapi">OpenAPI</option>
            <option value="graphql">GraphQL SDL</option>
            <option value="postman">Postman collection</option>
          </select>
        </div>
        {cfg.spec.kind !== 'none' && (
          <div className="space-y-2">
            <Label>Spec URL</Label>
            <Input
              value={cfg.spec.url ?? ''}
              onChange={(e) => onChange({ ...cfg, spec: { ...cfg.spec, url: e.target.value } })}
              placeholder="https://api.example.com/openapi.json"
            />
          </div>
        )}
      </div>

      {cfg.spec.kind !== 'none' && (
        <div className="space-y-2">
          <Label>Or upload a spec file</Label>
          <input
            type="file"
            accept=".json,.yaml,.yml,application/json,application/yaml"
            onChange={handleFileUpload}
            disabled={uploading}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-muted file:text-foreground hover:file:bg-muted"
          />
          {cfg.spec.uploadedKey && (
            <p className="text-xs text-emerald-700 dark:text-emerald-400">Uploaded: {cfg.spec.uploadedKey}</p>
          )}
          {uploadErr && <p className="text-xs text-red-700 dark:text-red-400">{uploadErr}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <input
            id="allowDestructive-settings"
            type="checkbox"
            checked={cfg.rulesOfEngagement.allowDestructive}
            onChange={(e) =>
              onChange({
                ...cfg,
                rulesOfEngagement: { ...cfg.rulesOfEngagement, allowDestructive: e.target.checked },
              })
            }
            className="h-4 w-4 rounded border-border"
          />
          <Label htmlFor="allowDestructive-settings" className="text-sm">
            Allow destructive tests
          </Label>
        </div>
        <div className="space-y-1">
          <Label>Rate limit (req/s)</Label>
          <Input
            type="number"
            min={1}
            max={100}
            value={cfg.rulesOfEngagement.rateLimitRps}
            onChange={(e) =>
              onChange({
                ...cfg,
                rulesOfEngagement: {
                  ...cfg.rulesOfEngagement,
                  rateLimitRps: parseInt(e.target.value, 10) || 1,
                },
              })
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Stop paths (one per line)</Label>
          <textarea
            value={(cfg.rulesOfEngagement.stopPaths ?? []).join('\n')}
            onChange={(e) =>
              onChange({
                ...cfg,
                rulesOfEngagement: {
                  ...cfg.rulesOfEngagement,
                  stopPaths: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean),
                },
              })
            }
            rows={3}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
            placeholder="/admin/destroy&#10;/internal/*"
          />
          <p className="text-[11px] text-muted-foreground">Agents short-circuit if they land on these.</p>
        </div>
        <div className="space-y-1">
          <Label>Focus paths (one per line)</Label>
          <textarea
            value={(cfg.rulesOfEngagement.focusPaths ?? []).join('\n')}
            onChange={(e) =>
              onChange({
                ...cfg,
                rulesOfEngagement: {
                  ...cfg.rulesOfEngagement,
                  focusPaths: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean),
                },
              })
            }
            rows={3}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
            placeholder="/v1/orders/*&#10;/v1/users/me"
          />
          <p className="text-[11px] text-muted-foreground">Agents prioritize these first.</p>
        </div>
      </div>

      <BusinessFlowsEditor cfg={cfg} onChange={onChange} />
      <SiblingEnvsEditor cfg={cfg} onChange={onChange} />
      <WebappConfigEditor cfg={cfg} onChange={onChange} />

      <Button disabled={saving} onClick={onSave} className="bg-emerald-600 hover:bg-emerald-500 text-white">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
        Save endpoint config
      </Button>
    </div>
  );
}

// ─── Principals manager ────────────────────────────────────────────────────
//
// Multi-user pentest setup. Each principal is a separate test identity
// the agents replay, with its own auth shape and encrypted credential
// envelope. Stored on `endpointConfig.principals` jsonb; credentials
// are AES-256-GCM envelopes keyed by the org's DEK.

type PrincipalListItem = {
  principalId: string;
  label: string;
  role: PrincipalRole;
  auth: { kind: EndpointAuthKind; headerName?: string; tokenEndpoint?: string; scopes?: string[] };
  ownedResources?: Array<{ resource: string; ids: string[] }>;
  notes?: string;
  credentialsConfigured: boolean;
};

function PrincipalsManager({
  orgId, portfolioId, projectId,
}: { orgId: string; portfolioId: string; projectId: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['principals', orgId, portfolioId, projectId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiClient.listPrincipals(orgId, portfolioId, projectId),
  });
  const principals = ((data?.data as PrincipalListItem[] | undefined) ?? []);

  const [adding, setAdding] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftRole, setDraftRole] = useState<PrincipalRole>('member');
  const [draftKind, setDraftKind] = useState<EndpointAuthKind>('bearer');

  const create = useMutation({
    mutationFn: () => apiClient.addPrincipal(orgId, portfolioId, projectId, {
      label: draftLabel.trim(),
      role: draftRole,
      auth: { kind: draftKind },
    }),
    onSuccess: () => {
      setAdding(false); setDraftLabel(''); setDraftRole('member'); setDraftKind('bearer');
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-foreground">Principals</p>
          <p className="text-[11px] text-muted-foreground">
            Test identities the agents replay. Add admin + member + viewer to surface
            BOLA / BFLA / privilege-escalation gaps.
          </p>
        </div>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>+ Add principal</Button>
        )}
      </div>

      {isLoading && <p className="text-xs text-muted-foreground">loading…</p>}

      {principals.map((p) => (
        <PrincipalRow
          key={p.principalId}
          principal={p}
          orgId={orgId}
          portfolioId={portfolioId}
          projectId={projectId}
          queryKey={queryKey}
        />
      ))}

      {adding && (
        <div className="space-y-3 rounded-md border border-dashed border-border bg-card p-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Label</Label>
              <Input value={draftLabel} onChange={(e) => setDraftLabel(e.target.value)} placeholder="Acme tenant admin" />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <select
                value={draftRole}
                onChange={(e) => setDraftRole(e.target.value as PrincipalRole)}
                className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
              >
                <option value="admin">admin</option>
                <option value="member">member</option>
                <option value="viewer">viewer</option>
                <option value="service">service</option>
                <option value="other">other</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Auth kind</Label>
              <select
                value={draftKind}
                onChange={(e) => setDraftKind(e.target.value as EndpointAuthKind)}
                className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground"
              >
                <option value="api-key">API key</option>
                <option value="bearer">Bearer token</option>
                <option value="basic">Basic auth</option>
                <option value="oauth2">OAuth 2.0</option>
                <option value="cookie">Session cookie</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={draftLabel.trim().length === 0 || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            {create.isError && <span className="text-xs text-red-700 dark:text-red-400">{(create.error as Error).message}</span>}
          </div>
        </div>
      )}

      {!isLoading && principals.length === 0 && !adding && (
        <p className="text-[11px] text-muted-foreground">
          No principals yet. Agents will run unauthenticated.
        </p>
      )}
    </div>
  );
}

function PrincipalRow({
  principal: p, orgId, portfolioId, projectId, queryKey,
}: {
  principal: PrincipalListItem;
  orgId: string; portfolioId: string; projectId: string;
  queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [headerName, setHeaderName] = useState(p.auth.headerName ?? '');
  const [tokenEndpoint, setTokenEndpoint] = useState(p.auth.tokenEndpoint ?? '');
  const [scopes, setScopes] = useState((p.auth.scopes ?? []).join(' '));

  const updateAuth = useMutation({
    mutationFn: () => apiClient.updatePrincipal(orgId, portfolioId, projectId, p.principalId, {
      auth: {
        kind: p.auth.kind,
        headerName: headerName || undefined,
        tokenEndpoint: tokenEndpoint || undefined,
        scopes: scopes ? scopes.split(/\s+/).filter(Boolean) : undefined,
      },
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const remove = useMutation({
    mutationFn: () => apiClient.deletePrincipal(orgId, portfolioId, projectId, p.principalId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return (
    <div className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/30"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-foreground">{p.label}</span>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {p.role}
          </span>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {p.auth.kind}
          </span>
          {p.credentialsConfigured ? (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-india-green/15 text-india-green">
              creds set
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-700 dark:text-yellow-500">
              no creds
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{expanded ? 'hide' : 'edit'}</span>
      </button>

      {expanded && (
        <div className="border-t border-border p-3 space-y-3">
          {(p.auth.kind === 'api-key' || p.auth.kind === 'cookie') && (
            <div className="space-y-1">
              <Label>{p.auth.kind === 'cookie' ? 'Cookie name' : 'Header name'}</Label>
              <div className="flex gap-2">
                <Input
                  value={headerName}
                  onChange={(e) => setHeaderName(e.target.value)}
                  placeholder={p.auth.kind === 'cookie' ? 'session' : 'X-API-Key'}
                />
                <Button size="sm" disabled={updateAuth.isPending} onClick={() => updateAuth.mutate()}>Save</Button>
              </div>
            </div>
          )}
          {p.auth.kind === 'oauth2' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Token endpoint</Label>
                <Input
                  value={tokenEndpoint}
                  onChange={(e) => setTokenEndpoint(e.target.value)}
                  placeholder="https://auth.example.com/oauth/token"
                />
              </div>
              <div className="space-y-1">
                <Label>Scopes (space-separated)</Label>
                <div className="flex gap-2">
                  <Input
                    value={scopes}
                    onChange={(e) => setScopes(e.target.value)}
                    placeholder="read:api write:api"
                  />
                  <Button size="sm" disabled={updateAuth.isPending} onClick={() => updateAuth.mutate()}>Save</Button>
                </div>
              </div>
            </div>
          )}

          <PrincipalCredentials
            orgId={orgId}
            portfolioId={portfolioId}
            projectId={projectId}
            principalId={p.principalId}
            kind={p.auth.kind}
            configured={p.credentialsConfigured}
            queryKey={queryKey}
          />

          <PrincipalOwnedResources
            orgId={orgId}
            portfolioId={portfolioId}
            projectId={projectId}
            principal={p}
            queryKey={queryKey}
          />

          <div className="pt-2 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (confirm(`Delete principal "${p.label}"? This removes its config and any stored credentials.`)) {
                  remove.mutate();
                }
              }}
              disabled={remove.isPending}
            >
              {remove.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete principal
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function PrincipalCredentials({
  orgId, portfolioId, projectId, principalId, kind, configured, queryKey,
}: {
  orgId: string; portfolioId: string; projectId: string; principalId: string;
  kind: EndpointAuthKind; configured: boolean; queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const save = useMutation({
    mutationFn: () => {
      if (kind === 'basic') {
        return apiClient.setPrincipalCredentials(orgId, portfolioId, projectId, principalId, { username, password });
      }
      if (kind === 'oauth2') {
        return apiClient.setPrincipalCredentials(orgId, portfolioId, projectId, principalId, { clientId, clientSecret });
      }
      return apiClient.setPrincipalCredentials(orgId, portfolioId, projectId, principalId, { value });
    },
    onSuccess: () => {
      setValue(''); setUsername(''); setPassword(''); setClientId(''); setClientSecret('');
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const clear = useMutation({
    mutationFn: () => apiClient.clearPrincipalCredentials(orgId, portfolioId, projectId, principalId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const canSave = (() => {
    if (kind === 'none') return false;
    if (kind === 'basic') return username.trim().length > 0 && password.trim().length > 0;
    if (kind === 'oauth2') return clientId.trim().length > 0 && clientSecret.trim().length > 0;
    return value.trim().length > 0;
  })();

  if (kind === 'none') return null;

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Credentials</p>
      {kind === 'basic' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Username</Label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="api-user" />
          </div>
          <div className="space-y-1">
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
        </div>
      ) : kind === 'oauth2' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Client ID</Label>
            <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="client-id" />
          </div>
          <div className="space-y-1">
            <Label>Client secret</Label>
            <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="••••••••" />
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <Label>{kind === 'bearer' ? 'Bearer token' : kind === 'api-key' ? 'API key value' : 'Cookie value'}</Label>
          <Input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={configured ? '••••••••  (replace existing)' : 'paste value here'}
          />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {configured ? 'Replace' : 'Save'}
        </Button>
        {configured && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (confirm('Clear stored credentials for this principal?')) clear.mutate();
            }}
            disabled={clear.isPending}
          >
            {clear.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Clear
          </Button>
        )}
        {save.isError && <span className="text-xs text-red-700 dark:text-red-400">{(save.error as Error).message}</span>}
      </div>
      <p className="text-[10px] text-muted-foreground">
        Encrypted with this org's DEK and stored on the project row. Never logged or echoed back.
      </p>
    </div>
  );
}

function PrincipalOwnedResources({
  orgId, portfolioId, projectId, principal: p, queryKey,
}: {
  orgId: string; portfolioId: string; projectId: string;
  principal: PrincipalListItem; queryKey: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const initial = (p.ownedResources ?? []).map((r) => `${r.resource}: ${r.ids.join(', ')}`).join('\n');
  const [text, setTextValue] = useState(initial);

  const save = useMutation({
    mutationFn: () => {
      const parsed: Array<{ resource: string; ids: string[] }> = text
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [resource, idsRaw] = line.split(':');
          return {
            resource: (resource ?? '').trim(),
            ids: (idsRaw ?? '').split(',').map((x) => x.trim()).filter(Boolean),
          };
        })
        .filter((r) => r.resource && r.ids.length > 0);
      return apiClient.updatePrincipal(orgId, portfolioId, projectId, p.principalId, {
        ownedResources: parsed,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return (
    <div className="space-y-1">
      <Label>Owned resource IDs</Label>
      <textarea
        value={text}
        onChange={(e) => setTextValue(e.target.value)}
        rows={3}
        className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
        placeholder="orders: ord_123, ord_456&#10;invoices: inv_001"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Save resources
        </Button>
        <p className="text-[10px] text-muted-foreground">
          One per line: <code>resource: id1, id2</code>. Used to seed BOLA / BFLA cross-tests.
        </p>
      </div>
    </div>
  );
}

// ─── Business flows editor ─────────────────────────────────────────────────

function BusinessFlowsEditor({
  cfg, onChange,
}: { cfg: EndpointConfig; onChange: (v: EndpointConfig) => void }) {
  const flows: BusinessFlow[] = cfg.businessFlows ?? [];
  function update(next: BusinessFlow[]) { onChange({ ...cfg, businessFlows: next }); }
  function add() {
    update([...flows, { flowId: crypto.randomUUID(), name: 'New flow', steps: [] }]);
  }
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Business flows</p>
          <p className="text-[11px] text-muted-foreground">
            Multi-step flows the agents replay end-to-end (checkout, password reset…) — surfaces API6 sensitive-flow weaknesses.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={add}>+ Add flow</Button>
      </div>
      {flows.map((f, i) => (
        <div key={f.flowId} className="space-y-2 rounded-md border border-border bg-card p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input
                value={f.name}
                onChange={(e) => {
                  const next = [...flows]; next[i] = { ...f, name: e.target.value }; update(next);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input
                value={f.description ?? ''}
                onChange={(e) => {
                  const next = [...flows]; next[i] = { ...f, description: e.target.value }; update(next);
                }}
                placeholder="What the flow proves"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Steps (one per line: METHOD /path [expectStatus])</Label>
            <textarea
              value={f.steps.map((s) => `${s.method} ${s.path}${s.expectStatus ? ' ' + s.expectStatus : ''}`).join('\n')}
              onChange={(e) => {
                const steps = e.target.value.split(/\n+/).map((line) => line.trim()).filter(Boolean).map((line) => {
                  const parts = line.split(/\s+/);
                  const status = parts[2] ? parseInt(parts[2], 10) : undefined;
                  return { method: parts[0] ?? 'GET', path: parts[1] ?? '/', expectStatus: Number.isFinite(status) ? status : undefined };
                });
                const next = [...flows]; next[i] = { ...f, steps }; update(next);
              }}
              rows={4}
              className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
              placeholder="POST /v1/cart 201&#10;POST /v1/checkout 200&#10;GET /v1/orders/{id} 200"
            />
          </div>
          <div className="space-y-1">
            <Label>Enforce (one rule per line)</Label>
            <textarea
              value={(f.enforce ?? []).join('\n')}
              onChange={(e) => {
                const enforce = e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean);
                const next = [...flows]; next[i] = { ...f, enforce }; update(next);
              }}
              rows={2}
              className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
              placeholder="rate-limit&#10;step-up auth on /checkout"
            />
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => update(flows.filter((x) => x.flowId !== f.flowId))}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Sibling environments editor ───────────────────────────────────────────

function SiblingEnvsEditor({
  cfg, onChange,
}: { cfg: EndpointConfig; onChange: (v: EndpointConfig) => void }) {
  const envs: SiblingEnvironment[] = cfg.siblingEnvironments ?? [];
  function update(next: SiblingEnvironment[]) { onChange({ ...cfg, siblingEnvironments: next }); }
  function add() { update([...envs, { envId: crypto.randomUUID(), label: 'staging', baseUrl: '' }]); }
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Sibling environments</p>
          <p className="text-[11px] text-muted-foreground">
            Staging / sandbox URLs — agents probe drift between deployments (API9 inventory management).
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={add}>+ Add env</Button>
      </div>
      {envs.map((e, i) => (
        <div key={e.envId} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end rounded-md border border-border bg-card p-3">
          <div className="space-y-1">
            <Label>Label</Label>
            <Input
              value={e.label}
              onChange={(ev) => {
                const next = [...envs]; next[i] = { ...e, label: ev.target.value }; update(next);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label>Base URL</Label>
            <Input
              value={e.baseUrl}
              onChange={(ev) => {
                const next = [...envs]; next[i] = { ...e, baseUrl: ev.target.value }; update(next);
              }}
              placeholder="https://staging.api.example.com"
            />
          </div>
          <div className="flex gap-2">
            <Input
              value={e.expected ?? ''}
              onChange={(ev) => {
                const next = [...envs]; next[i] = { ...e, expected: ev.target.value }; update(next);
              }}
              placeholder="no real customer data"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => update(envs.filter((x) => x.envId !== e.envId))}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Webapp pentest config ─────────────────────────────────────────────────

function WebappConfigEditor({
  cfg, onChange,
}: { cfg: EndpointConfig; onChange: (v: EndpointConfig) => void }) {
  const w: WebappPentestConfig = cfg.webappConfig ?? {};
  function update(next: WebappPentestConfig) { onChange({ ...cfg, webappConfig: next }); }
  const login = w.loginFlow ?? { url: '' };

  return (
    <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Web-app pentest config</p>
        <p className="text-[11px] text-muted-foreground">
          Drives the webapp-pentest skill. Optional — leave blank for pure API targets.
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Login flow</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label>Login URL</Label>
            <Input
              value={login.url}
              onChange={(e) => update({ ...w, loginFlow: { ...login, url: e.target.value } })}
              placeholder="https://app.example.com/login"
            />
          </div>
          <div className="space-y-1">
            <Label>Success indicator (selector or text)</Label>
            <Input
              value={login.successIndicator ?? ''}
              onChange={(e) => update({ ...w, loginFlow: { ...login, successIndicator: e.target.value } })}
              placeholder="#dashboard, or 'Welcome,'"
            />
          </div>
          <div className="space-y-1">
            <Label>Username selector</Label>
            <Input
              value={login.usernameSelector ?? ''}
              onChange={(e) => update({ ...w, loginFlow: { ...login, usernameSelector: e.target.value } })}
              placeholder="input[name=email]"
            />
          </div>
          <div className="space-y-1">
            <Label>Password selector</Label>
            <Input
              value={login.passwordSelector ?? ''}
              onChange={(e) => update({ ...w, loginFlow: { ...login, passwordSelector: e.target.value } })}
              placeholder="input[name=password]"
            />
          </div>
          <div className="space-y-1">
            <Label>Submit selector</Label>
            <Input
              value={login.submitSelector ?? ''}
              onChange={(e) => update({ ...w, loginFlow: { ...login, submitSelector: e.target.value } })}
              placeholder="button[type=submit]"
            />
          </div>
          <div className="space-y-1">
            <Label>Principal label (whose creds to use)</Label>
            <Input
              value={login.principalId ?? ''}
              onChange={(e) => update({ ...w, loginFlow: { ...login, principalId: e.target.value } })}
              placeholder="paste a principalId from above"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Skip these forms (one CSS selector per line)</Label>
          <textarea
            value={(w.skipForms ?? []).join('\n')}
            onChange={(e) =>
              update({ ...w, skipForms: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean) })
            }
            rows={3}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
            placeholder="form#delete-account&#10;form.payment"
          />
        </div>
        <div className="space-y-1">
          <Label>Authenticated paths (must require login)</Label>
          <textarea
            value={(w.authenticatedPaths ?? []).join('\n')}
            onChange={(e) =>
              update({ ...w, authenticatedPaths: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean) })
            }
            rows={3}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
            placeholder="/dashboard&#10;/settings/*"
          />
        </div>
        <div className="space-y-1">
          <Label>Focus paths</Label>
          <textarea
            value={(w.focusPaths ?? []).join('\n')}
            onChange={(e) =>
              update({ ...w, focusPaths: e.target.value.split(/\n+/).map((s) => s.trim()).filter(Boolean) })
            }
            rows={3}
            className="w-full rounded-md border border-border bg-card text-sm px-3 py-2 text-foreground font-mono"
            placeholder="/orders/*"
          />
        </div>
        <div className="space-y-1">
          <Label>Max crawl depth</Label>
          <Input
            type="number"
            min={1}
            max={10}
            value={w.maxCrawlDepth ?? ''}
            onChange={(e) =>
              update({ ...w, maxCrawlDepth: parseInt(e.target.value, 10) || undefined })
            }
            placeholder="3"
          />
        </div>
      </div>
    </div>
  );
}
